import React, { useState } from 'react';
import { KeyRound, Check } from 'lucide-react';
import { auth } from '../../config/firebase';
import { useAuth, ProveedorSocial, ID_PROVEEDOR, NOMBRE_PROVEEDOR } from '../../context/AuthContext';
import { LogoApple, LogoGoogle } from '../AccesoSocial';
import { esCancelacion, mensajeErrorAccesoSocial, mensajeErrorAuth } from '../../utils/authErrores';

const SOCIALES: ProveedorSocial[] = ['apple', 'google'];

/**
 * Con qué se puede entrar en la cuenta: contraseña, Apple, Google. Se puede
 * añadir uno nuevo o quitar uno, salvo el último.
 */
export const MetodosAcceso: React.FC = () => {
  // Se mira auth.currentUser y no el `user` del contexto: este último puede
  // ser una copia hecha al cambiar el nombre, sin los métodos al día.
  const { vincular, desvincular } = useAuth();
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [error, setError] = useState('');

  const actual = auth.currentUser;
  if (!actual) return null;
  const ids = actual.providerData.map(p => p.providerId);
  const emailDe = (providerId: string) =>
    actual.providerData.find(p => p.providerId === providerId)?.email ?? undefined;
  const unico = ids.length <= 1;

  const accion = async (clave: string, fn: () => Promise<void>, proveedor?: ProveedorSocial) => {
    setError(''); setOcupado(clave);
    try { await fn(); }
    catch (e) {
      if (esCancelacion(e)) return;
      const porDefecto = 'No se ha podido cambiar el método de acceso.';
      setError(proveedor ? mensajeErrorAccesoSocial(e, NOMBRE_PROVEEDOR[proveedor], porDefecto) : mensajeErrorAuth(e, porDefecto));
    }
    finally { setOcupado(null); }
  };

  const fila = (clave: string, icono: React.ReactNode, nombre: string, vinculado: boolean, detalle: string | undefined,
                onVincular?: () => void) => (
    <li key={clave} className="flex items-center gap-3 py-2.5">
      <div className="w-9 h-9 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0
                      text-gray-900 dark:text-white">
        {icono}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-900 dark:text-white">{nombre}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
          {vinculado ? (detalle || 'Vinculado') : 'Sin vincular'}
        </p>
      </div>
      {vinculado ? (
        <button onClick={() => accion(clave, () => desvincular(clave))}
          disabled={unico || ocupado !== null}
          title={unico ? 'Es tu único método de acceso' : undefined}
          className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600
                     text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700
                     disabled:opacity-40 disabled:cursor-not-allowed transition">
          {ocupado === clave ? 'Quitando…' : 'Quitar'}
        </button>
      ) : onVincular ? (
        <button onClick={onVincular} disabled={ocupado !== null}
          className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white
                     disabled:opacity-50 transition">
          {ocupado === clave ? 'Vinculando…' : 'Vincular'}
        </button>
      ) : null}
    </li>
  );

  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Métodos de acceso</h3>
      <p className="text-xs text-gray-400 dark:text-gray-500 mb-2">
        Puedes entrar con cualquiera de los vinculados; tus datos son los mismos.
      </p>
      <ul className="divide-y divide-gray-100 dark:divide-gray-700">
        {/* La contraseña no se puede añadir desde aquí: solo se muestra si la hay */}
        {ids.includes('password') &&
          fila('password', <KeyRound className="w-4 h-4" />, 'Email y contraseña', true, emailDe('password'))}
        {SOCIALES.map(p => {
          const id = ID_PROVEEDOR[p];
          return fila(
            id,
            p === 'apple' ? <LogoApple className="w-3.5 h-4" /> : <LogoGoogle className="w-4 h-4" />,
            NOMBRE_PROVEEDOR[p],
            ids.includes(id),
            emailDe(id),
            () => accion(id, () => vincular(p), p),
          );
        })}
      </ul>
      {error && <p className="text-xs text-red-600 dark:text-red-400 mt-1">{error}</p>}
      {!error && unico && (
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 flex items-center gap-1">
          <Check className="w-3 h-3" /> Vincula otro método para poder entrar aunque pierdas este.
        </p>
      )}
    </div>
  );
};
